import axios from 'axios';
import { useCallback, useState, useEffect } from 'react';
import { useAsyncEffect, useLocalstorageState } from 'rooks';
import Cookies from 'js-cookie';
import { useAuth0 } from '@auth0/auth0-react';
import { getAuth0Audience } from '@/helpers/auth0';
import { useResetMixpanel } from '@/app/mixpanel/_hooks';
import { useSelf } from '@/data/user';

const authStrategy = process.env.VITE_AUTH_STRATEGY;

export const useLogout = () => {
  const { logout } = useAuth0();
  const resetMixpanel = useResetMixpanel();

  const handleLogout = useCallback(async () => {
    resetMixpanel();
    if (authStrategy === 'zebel-api') {
      Cookies.remove('token');
      window.location.href = '/login';
    } else {
      await logout();
    }
  }, [logout, resetMixpanel]);

  return handleLogout;
};

export const useGetAuthorizationToken = () => {
  const { getAccessTokenSilently } = useAuth0();

  const getAuthorizationToken = useCallback(async () => {
    if (authStrategy === 'zebel-api') {
      return Cookies.get('token');
    } else {
      const apiToken = await getAccessTokenSilently({ audience: getAuth0Audience() });
      return apiToken;
    }
  }, [getAccessTokenSilently]);

  return getAuthorizationToken;
};

const useRedirectToLogin = () => {
  const { loginWithRedirect } = useAuth0();

  const redirectToLogin = useCallback(async () => {
    if (authStrategy === 'zebel-api') {
      window.location.href = '/login';
    } else {
      await loginWithRedirect();
    }
  }, [loginWithRedirect]);

  return redirectToLogin;
};

export const useInitializeAuthentication = () => {
  const { isAuthenticated: isAuthenticatedAuth0, isLoading } = useAuth0();
  const [isAxiosInitialized, setIsAxiosInitialized] = useState(false);
  const getAuthorizationToken = useGetAuthorizationToken();
  const isAuthenticated = authStrategy === 'zebel-api' ? Cookies.get('token') !== undefined : isAuthenticatedAuth0;
  const redirectToLogin = useRedirectToLogin();

  useAsyncEffect(async () => {
    if (isLoading) return;

    if (!isAuthenticated) {
      await redirectToLogin();
      return;
    }

    const reqInterceptor = axios.interceptors.request.use(async (config) => {
      const token = await getAuthorizationToken();
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    setIsAxiosInitialized(true);

    return () => {
      axios.interceptors.request.eject(reqInterceptor);
    };
  }, [isLoading, isAuthenticated]);

  return {
    isAuthenticated,
    isLoading: isLoading || !isAxiosInitialized
  };
};

export const useInitializeZebelerAuthentication = () => {
  const [activeCompanyUid] = useLocalstorageState('active-company-uid', null);
  const self = useSelf(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    axios.interceptors.request.use(async (config) => {
      if (self.data.is_zebeler && activeCompanyUid) {
        config.headers['company-uid'] = activeCompanyUid;
      } else {
        config.headers['company-uid'] = '';
      }
      return config;
    });

    setIsInitialized(true);
  }, [self.data.is_zebeler, activeCompanyUid]);

  return {
    isInitialized
  };
};
